"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import periodicTableData from "@/lib/data/periodic-table";

type PeriodicElement = {
  name: string;
  appearance: string | null;
  atomic_mass: number | null;
  boil: number | null;
  category: string;
  density: number | null;
  discovered_by: string | null;
  melt: number | null;
  molar_heat: number | null;
  named_by: string | null;
  number: number; // atomic number
  period: number; // row
  group: number | null; // column group may be null for some
  phase: string | null;
  source: string | null;
  bohr_model_image?: string | null;
  bohr_model_3d?: string | null;
  spectral_img?: string | null;
  summary: string;
  symbol: string;
  xpos: number; // visual column 1..18
  ypos: number; // visual row
  wxpos?: number | null;
  wypos?: number | null;
  shells?: number[];
  electron_configuration?: string | null;
  electron_configuration_semantic?: string | null;
  electron_affinity?: number | null;
  electronegativity_pauling?: number | null;
  ionization_energies?: number[];
  [key: string]: unknown;
};

type PeriodicData = {
  elements: PeriodicElement[];
};

type ElectronRingsProps = {
  shells: number[];
  className?: string;
};

function ElectronRings({ shells, className }: ElectronRingsProps) {
  // Guard: nothing to render
  if (!shells || shells.length === 0) return null;

  // Use a normalized 100x100 viewBox to keep sizing ratio-based
  const size = 100;
  const center = 50;
  const maxRadius = 46; // leave padding inside viewBox
  const minRadius = 12; // nucleus padding
  const ringCount = shells.length;
  const step = ringCount > 1 ? (maxRadius - minRadius) / (ringCount - 1) : 0;

  return (
    <div className={className} aria-label="Electron shells visualization">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-hidden="false"
        className="w-full h-auto"
      >
        {/* Nucleus */}
        <circle cx={center} cy={center} r={8} className="fill-accent/20" />

        {shells.map((electronCount, shellIndex) => {
          const radius = minRadius + step * shellIndex;
          const durationSeconds = 12 + shellIndex * 6; // slower for outer shells
          const electrons = Math.max(0, electronCount | 0);
          const angleStep = electrons > 0 ? (Math.PI * 2) / electrons : 0;
          return (
            <g
              key={`shell-${shellIndex}`}
              // Rotate the whole ring group; respect reduced motion
              className="motion-safe:animate-spin motion-reduce:animate-none"
              style={{
                animationDuration: `${durationSeconds}s`,
                transformOrigin: "50% 50%",
                transformBox: "fill-box",
              }}
            >
              {/* Ring */}
              <circle
                cx={center}
                cy={center}
                r={radius}
                className="fill-none stroke-border/70"
                strokeWidth={0.6}
              />
              {/* Electrons */}
              {Array.from({ length: electrons }).map((_, electronIndex) => {
                const angle = electronIndex * angleStep;
                const x = center + radius * Math.cos(angle);
                const y = center + radius * Math.sin(angle);
                return (
                  <circle
                    key={`e-${shellIndex}-${electronIndex}`}
                    cx={x}
                    cy={y}
                    r={1.8}
                    className="fill-foreground/80"
                  />
                );
              })}
            </g>
          );
        })}
        <title>Electron shells and electrons per shell</title>
      </svg>
    </div>
  );
}

function usePeriodicData() {
  const [data, setData] = useState<PeriodicData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Use imported data instead of fetching to avoid path issues in npm package
    try {
      setData(periodicTableData as PeriodicData);
      setLoading(false);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Unknown error";
      setError(message);
      setLoading(false);
    }
  }, []);

  return { data, loading, error };
}

function normalizeCategory(category: string | null): string {
  if (!category) return "unknown";
  return category.toLowerCase();
}

const CATEGORY_BG: Record<string, string> = {
  "alkali metal": "bg-(--color-pt-alkali)",
  "alkaline earth metal": "bg-(--color-pt-alkaline)",
  "transition metal": "bg-(--color-pt-transition)",
  "post-transition metal": "bg-(--color-pt-post)",
  metalloid: "bg-(--color-pt-metalloid)",
  "diatomic nonmetal": "bg-(--color-pt-diatomic)",
  "polyatomic nonmetal": "bg-(--color-pt-polyatomic)",
  "noble gas": "bg-(--color-pt-noble)",
  lanthanide: "bg-(--color-pt-lanthanide)",
  actinide: "bg-(--color-pt-actinide)",
  unknown: "bg-(--color-pt-unknown)",
};

export default function PeriodicTable() {
  const { data, loading, error } = usePeriodicData();
  const [query, setQuery] = useState<string>("");
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const liveRef = useRef<HTMLDivElement | null>(null);

  const allElements = useMemo(() => data?.elements ?? [], [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allElements;
    return allElements.filter((el) => {
      return (
        el.name.toLowerCase().includes(q) ||
        el.symbol.toLowerCase().includes(q) ||
        String(el.number).includes(q) ||
        normalizeCategory(el.category).includes(q)
      );
    });
  }, [allElements, query]);

  // Build a lookup by (y,x) for grid placement
  const { maxX, maxY, gridMap } = useMemo(() => {
    let maxXLocal = 18;
    let maxYLocal = 10; // allow lanthanides/actinides rows
    const map = new Map<string, PeriodicElement>();
    for (const el of allElements) {
      const x = el.xpos ?? el.group ?? 1;
      const y = el.ypos ?? el.period ?? 1;
      const key = `${y}:${x}`;
      map.set(key, el);
      if (x > maxXLocal) maxXLocal = x;
      if (y > maxYLocal) maxYLocal = y;
    }
    return { maxX: maxXLocal, maxY: maxYLocal, gridMap: map };
  }, [allElements]);

  const selected = useMemo(() => {
    if (selectedNumber == null) return null;
    return allElements.find((e) => e.number === selectedNumber) ?? null;
  }, [allElements, selectedNumber]);

  useEffect(() => {
    if (selected && liveRef.current) {
      liveRef.current.textContent = `${selected.name} selected`;
    }
  }, [selected]);

  const onKeyNav = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, el: PeriodicElement) => {
      const key = event.key;
      const currentX = el.xpos ?? el.group ?? 1;
      const currentY = el.ypos ?? el.period ?? 1;
      let nextX = currentX;
      let nextY = currentY;
      if (key === "ArrowRight") nextX = Math.min(currentX + 1, maxX);
      else if (key === "ArrowLeft") nextX = Math.max(currentX - 1, 1);
      else if (key === "ArrowDown") nextY = Math.min(currentY + 1, maxY);
      else if (key === "ArrowUp") nextY = Math.max(currentY - 1, 1);
      else return;
      event.preventDefault();
      const nextKey = `${nextY}:${nextX}`;
      const nextEl = gridMap.get(nextKey);
      if (!nextEl) return;
      const btn = gridRef.current?.querySelector<HTMLButtonElement>(
        `button[data-ax="${nextEl.number}"]`
      );
      btn?.focus();
    },
    [gridMap, maxX, maxY]
  );

  return (
    <div
      className="flex flex-col md:flex-row gap-3 h-full"
      aria-label="Periodic table tool"
    >
      <div className="md:w-2/3 lg:w-3/4 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <label htmlFor="pt-search" className="text-sm font-medium">
            Search
          </label>
          <input
            id="pt-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name, symbol, number, category"
            className="w-full md:w-80 h-9 rounded-md border border-border bg-background px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-describedby="pt-search-help"
          />
        </div>
        <div id="pt-search-help" className="sr-only">
          Type to filter by element name, symbol, atomic number, or category.
        </div>

        {loading && (
          <div role="status" aria-live="polite" className="text-sm opacity-80">
            Loading periodic data...
          </div>
        )}
        {error && (
          <div role="alert" className="text-sm text-red-600">
            {error}
          </div>
        )}

        <div
          ref={gridRef}
          role="grid"
          aria-label="Periodic table grid"
          className="grid gap-1"
          style={{ gridTemplateColumns: `repeat(18, minmax(2rem, 1fr))` }}
        >
          {Array.from({ length: maxY }).map((_, rowIdx) => {
            const row = rowIdx + 1;
            return (
              <div key={`row-${row}`} role="row" className="contents">
                {Array.from({ length: 18 }).map((__, colIdx) => {
                  const col = colIdx + 1;
                  const element = gridMap.get(`${row}:${col}`) ?? null;
                  const isHiddenByFilter =
                    element &&
                    !filtered.some((f) => f.number === element.number);
                  if (!element) {
                    return (
                      <div
                        key={`cell-${row}-${col}`}
                        role="gridcell"
                        aria-disabled="true"
                        className="h-12"
                      />
                    );
                  }
                  const selectedState = selected?.number === element.number;
                  const cat = normalizeCategory(element.category);
                  const color = CATEGORY_BG[cat] ?? CATEGORY_BG["unknown"];
                  return (
                    <button
                      key={`cell-${row}-${col}`}
                      role="gridcell"
                      aria-selected={selectedState}
                      data-ax={element.number}
                      onClick={() => setSelectedNumber(element.number)}
                      onKeyDown={(e) => onKeyNav(e, element)}
                      className={`h-12 rounded-md border text-left px-2 py-1 overflow-hidden border-border ${color} ${
                        isHiddenByFilter ? "opacity-30" : ""
                      } focus:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
                      title={`${element.name} (${element.symbol})`}
                    >
                      <div className="text-[10px] leading-none opacity-70">
                        {element.number}
                      </div>
                      <div className="text-sm font-semibold leading-tight">
                        {element.symbol}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div ref={liveRef} aria-live="polite" className="sr-only" />
      </div>

      <div
        className="md:w-1/3 lg:w-1/4 border rounded-md border-border p-3 overflow-auto"
        aria-label="Element details"
        role="region"
      >
        {!selected && (
          <div className="text-sm opacity-80">
            Select an element to see details.
          </div>
        )}
        {selected && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs opacity-70">
                  Atomic #{selected.number}
                </div>
                <div className="text-lg font-semibold">{selected.name}</div>
              </div>
              <div className="text-2xl font-bold" aria-label="Symbol">
                {selected.symbol}
              </div>
            </div>
            {/* Electron shells visualization */}
            {Array.isArray(selected.shells) && selected.shells.length > 0 && (
              <ElectronRings
                shells={selected.shells}
                className="w-full mx-auto"
              />
            )}
            <div className="text-sm opacity-80">{selected.summary}</div>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              <dt className="opacity-70">Category</dt>
              <dd>{selected.category}</dd>
              <dt className="opacity-70">Phase</dt>
              <dd>{selected.phase ?? "—"}</dd>
              <dt className="opacity-70">Atomic mass</dt>
              <dd>{selected.atomic_mass ?? "—"}</dd>
              <dt className="opacity-70">Density</dt>
              <dd>{selected.density ?? "—"}</dd>
              <dt className="opacity-70">Melt</dt>
              <dd>{selected.melt ?? "—"}</dd>
              <dt className="opacity-70">Boil</dt>
              <dd>{selected.boil ?? "—"}</dd>
              <dt className="opacity-70">Electronegativity</dt>
              <dd>{selected.electronegativity_pauling ?? "—"}</dd>
              <dt className="opacity-70">Electron affinity</dt>
              <dd>{selected.electron_affinity ?? "—"}</dd>
              <dt className="opacity-70">Configuration</dt>
              <dd
                className="truncate"
                title={selected.electron_configuration ?? undefined}
              >
                {selected.electron_configuration ?? "—"}
              </dd>
            </dl>
            {selected.source && (
              <a
                href={selected.source}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-accent underline text-sm"
              >
                Learn more
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
